import { useEffect, useState } from 'react';
import { MessageSquare, Mail, Building, DollarSign, Clock, CheckCircle, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import AdminSidebar from '../components/AdminSidebar';
import api from '../lib/api';

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await api.get('/admin/inquiries');
      setInquiries(response.data);
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}?status=${status}`);
      toast.success('Status updated');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success('Inquiry deleted');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const typeConfig = {
    info: { label: 'Info Request', color: 'bg-cyan-500/20 text-cyan-400', icon: MessageSquare },
    buy: { label: 'Buy Interest', color: 'bg-violet-500/20 text-violet-400', icon: DollarSign },
    invest: { label: 'Investment', color: 'bg-emerald-500/20 text-emerald-400', icon: DollarSign },
  };

  const statusConfig = {
    new: { label: 'New', color: 'bg-amber-500/20 text-amber-400', icon: AlertCircle },
    contacted: { label: 'Contacted', color: 'bg-cyan-500/20 text-cyan-400', icon: Mail },
    closed: { label: 'Closed', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  };

  const filteredInquiries = inquiries.filter(inq => {
    if (filter === 'all') return true;
    return inq.inquiry_type === filter || inq.status === filter;
  });

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-inquiries-page">
      <AdminSidebar />
      
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Inquiries</h1>
            <p className="text-slate-400">Manage incoming interest and requests</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 bg-slate-900/50 border-white/10 text-white" data-testid="inquiry-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              <SelectItem value="all" className="text-white">All Inquiries</SelectItem>
              <SelectItem value="info" className="text-white">Info Requests</SelectItem>
              <SelectItem value="buy" className="text-white">Buy Interest</SelectItem>
              <SelectItem value="invest" className="text-white">Investment</SelectItem>
              <SelectItem value="new" className="text-white">Status: New</SelectItem>
              <SelectItem value="contacted" className="text-white">Status: Contacted</SelectItem>
              <SelectItem value="closed" className="text-white">Status: Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Inquiries List */}
        <div className="space-y-4" data-testid="inquiries-list">
          {loading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="h-32 rounded-xl skeleton" />
            ))
          ) : filteredInquiries.length > 0 ? (
            filteredInquiries.map((inquiry) => {
              const typeInfo = typeConfig[inquiry.inquiry_type] || typeConfig.info;
              const statusInfo = statusConfig[inquiry.status] || statusConfig.new;
              const TypeIcon = typeInfo.icon;
              const StatusIcon = statusInfo.icon;

              return (
                <div
                  key={inquiry.id}
                  className="p-6 rounded-xl bg-slate-900/50 border border-white/10"
                  data-testid={`inquiry-${inquiry.id}`}
                >
                  {/* Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Badge className={typeInfo.color}>
                        <TypeIcon className="h-3 w-3 mr-1" />
                        {typeInfo.label}
                      </Badge>
                      <Badge className={statusInfo.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Clock className="h-4 w-4" />
                      {new Date(inquiry.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>

                  {/* Idea Reference */}
                  <p className="text-xs text-slate-500 mb-2">
                    Regarding: <span className="text-cyan-400">{inquiry.idea_title}</span>
                  </p>

                  {/* Contact Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded bg-slate-800">
                        <MessageSquare className="h-4 w-4 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{inquiry.name}</p>
                        <p className="text-xs text-slate-500">Name</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded bg-slate-800">
                        <Mail className="h-4 w-4 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-white">{inquiry.email}</p>
                        <p className="text-xs text-slate-500">Email</p>
                      </div>
                    </div>
                    {inquiry.company && (
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded bg-slate-800">
                          <Building className="h-4 w-4 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-white">{inquiry.company}</p>
                          <p className="text-xs text-slate-500">Company</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Budget if provided */}
                  {inquiry.budget && (
                    <div className="flex items-center gap-2 mb-4 p-3 rounded bg-emerald-500/10 border border-emerald-500/20">
                      <DollarSign className="h-4 w-4 text-emerald-400" />
                      <span className="text-emerald-400 font-medium">{inquiry.budget}</span>
                      <span className="text-slate-500 text-sm">
                        {inquiry.inquiry_type === 'buy' ? 'Budget' : 'Investment Amount'}
                      </span>
                    </div>
                  )}

                  {/* Message */}
                  <div className="p-4 rounded-lg bg-slate-950/50 border border-white/5 mb-4">
                    <p className="text-slate-300 text-sm whitespace-pre-wrap">{inquiry.message}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-slate-500 mr-2">Update status:</span>
                      {Object.entries(statusConfig).map(([key, config]) => (
                        <Button
                          key={key}
                          variant="ghost"
                          size="sm"
                          onClick={() => updateStatus(inquiry.id, key)}
                          className={`text-sm ${
                            inquiry.status === key
                              ? config.color
                              : 'text-slate-500 hover:text-white'
                          }`}
                          data-testid={`set-status-${key}-${inquiry.id}`}
                        >
                          {config.label}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteInquiry(inquiry.id)}
                      className="text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-inquiry-${inquiry.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-16" data-testid="no-inquiries">
              <MessageSquare className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No inquiries yet</p>
              <p className="text-slate-500 text-sm">Inquiries will appear here when visitors express interest in your ideas</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminInquiries;
